import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  MessageSquare,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Filter,
  Search,
  TrendingUp,
  Calendar,
  User,
  Building,
  Globe,
  MoreHorizontal,
  ArrowUpRight,
  Edit3,
  AlertTriangle,
  ChevronDown,
  Activity,
  Archive,
  Zap,
  Flag,
  X,
  ExternalLink,
  Image as ImageIcon,
  Edit,
  Trash2
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { formatRelativeTime } from '../../utils/format'
import { cn } from '../../utils/cn'
import ModernDateTimeInput from '../ui/ModernDateTimeInput'

const StatusBadge = ({ status }) => {
  const variants = {
    pending: { icon: Clock, className: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900 dark:text-warning-200 dark:border-warning-800' },
    approved: { icon: CheckCircle, className: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900 dark:text-success-200 dark:border-success-800' },
    rejected: { icon: XCircle, className: 'bg-error-100 text-error-800 border-error-200 dark:bg-error-900 dark:text-error-200 dark:border-error-800' },
    completed: { icon: CheckCircle, className: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900 dark:text-success-200 dark:border-success-800' }
  }
  const variant = variants[status] || variants.pending
  const Icon = variant.icon
  return (
    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', variant.className)}>
      <Icon className="h-3 w-3 mr-1" />
      {status?.toUpperCase() || 'PENDING'}
    </span>
  )
}

const PriorityBadge = ({ priority }) => {
  const colors = {
    low: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700',
    medium: 'bg-accent-100 text-accent-700 border-accent-200 dark:bg-accent-900 dark:text-accent-200 dark:border-accent-800',
    high: 'bg-primary-100 text-primary-700 border-primary-200 dark:bg-primary-900 dark:text-primary-200 dark:border-primary-800',
    urgent: 'bg-error-100 text-error-700 border-error-200 dark:bg-error-900 dark:text-error-200 dark:border-error-800'
  }
  const icons = { low: null, medium: ArrowUpRight, high: TrendingUp, urgent: AlertTriangle }
  const Icon = icons[priority]
  return (
    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', colors[priority] || colors.medium)}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {priority?.toUpperCase() || 'MEDIUM'}
    </span>
  )
}

const CategoryBadge = ({ category }) => {
  const colors = {
    content: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
    feature: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200',
    bug: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200',
    design: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
  }
  const icons = { content: Edit3, feature: Zap, bug: AlertTriangle, design: Eye }
  const Icon = icons[category]
  return (
    <span className={cn('inline-flex items-center px-2 py-1 rounded-md text-xs font-medium', colors[category] || colors.content)}>
      {Icon && <Icon className="h-3 w-3 mr-1" />}
      {category?.toUpperCase() || 'CONTENT'}
    </span>
  )
}

const ActionMenu = ({ request, onApprove, onReject, onComplete, isCompleting }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const hasActions = request.status === 'pending' || ['approved', 'in_progress'].includes(request.status)
  if (!hasActions) return null

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        onClick={() => setOpen(!open)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-[#221f2f] border border-neutral-200 dark:border-[#2f2b41] rounded-lg shadow-lg z-20 overflow-hidden py-1">
          {request.status === 'pending' && (
            <>
              <button
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-success-600 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors"
                onClick={() => { setOpen(false); onApprove(request) }}
              >
                <ThumbsUp className="h-3.5 w-3.5" /> Approve
              </button>
              <button
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors"
                onClick={() => { setOpen(false); onReject(request) }}
              >
                <ThumbsDown className="h-3.5 w-3.5" /> Reject
              </button>
            </>
          )}
          {['approved', 'in_progress'].includes(request.status) && (
            <button
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-accent-600 dark:text-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors disabled:opacity-50"
              onClick={() => { setOpen(false); onComplete(request.id) }}
              disabled={isCompleting}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Mark Complete
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const RequestManager = () => {
  const [filterStatus, setFilterStatus] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [showApprovalModal, setShowApprovalModal] = useState(null)
  const [showRejectionModal, setShowRejectionModal] = useState(null)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Auto-open a specific request when ?open=id is in the URL
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedRequestId(openId)
  }, [searchParams])

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery])

  const { data: requests, isLoading } = useQuery(
    ['requests', filterStatus, filterPriority],
    () => apiService.get('/requests', { 
      params: { 
        status: filterStatus, 
        priority: filterPriority
      } 
    }).then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Approve request mutation
  const approveMutation = useMutation(
    ({ requestId, notes, estimatedHours, deadline, assignedTo }) => 
      apiService.post(`/requests/${requestId}/approve`, { 
        approval_notes: notes,
        estimated_hours: estimatedHours,
        deadline,
        assigned_to: assignedTo
      }),
    {
      onSuccess: () => {
        toast.success('Request approved successfully!')
        queryClient.invalidateQueries('requests')
        setShowApprovalModal(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve request')
      }
    }
  )

  // Reject request mutation
  const rejectMutation = useMutation(
    ({ requestId, reason }) => apiService.post(`/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        toast.success('Request rejected successfully!')
        queryClient.invalidateQueries('requests')
        setShowRejectionModal(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject request')
      }
    }
  )

  // Complete request mutation
  const completeMutation = useMutation(
    (requestId) => apiService.post(`/requests/${requestId}/complete`),
    {
      onSuccess: () => {
        toast.success('Request marked as completed!')
        queryClient.invalidateQueries('requests')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to complete request')
      }
    }
  )

  const mockRequests = [
    {
      id: 1,
      title: 'Update homepage hero section',
      description: 'Please change the hero text and add a new CTA button to improve conversion rates. The current copy is outdated and needs to reflect our new brand messaging.',
      site_name: 'Acme Corp Website',
      company_name: 'Acme Corporation',
      created_by_name: 'John Doe',
      status: 'pending',
      priority: 'high',
      created_at: '2024-01-15T10:30:00Z',
      urgency: 'high',
      category: 'content'
    },
    {
      id: 2,
      title: 'Add new product page',
      description: 'Create a new page for our latest product launch with detailed specifications and pricing.',
      site_name: 'Tech Solutions Site',
      company_name: 'Tech Solutions Inc',
      created_by_name: 'Jane Smith',
      status: 'approved',
      priority: 'medium',
      created_at: '2024-01-14T15:20:00Z',
      urgency: 'medium',
      category: 'feature'
    },
    {
      id: 3,
      title: 'Fix contact form bug',
      description: 'The contact form is not sending emails properly. Users are complaining about not receiving confirmation emails.',
      site_name: 'Business Pro Site',
      company_name: 'Business Pro LLC',
      created_by_name: 'Mike Johnson',
      status: 'rejected',
      priority: 'urgent',
      created_at: '2024-01-13T09:15:00Z',
      urgency: 'urgent',
      category: 'bug'
    },
    {
      id: 4,
      title: 'Update pricing table',
      description: 'Update the pricing table with new packages and features.',
      site_name: 'SaaS Platform',
      company_name: 'SaaS Solutions',
      created_by_name: 'Sarah Wilson',
      status: 'completed',
      priority: 'low',
      created_at: '2024-01-12T14:45:00Z',
      urgency: 'low',
      category: 'content'
    }
  ]

  const displayRequests = requests || mockRequests

  // Filter requests based on search query
  const filteredRequests = displayRequests.filter(request => {
    const matchesSearch = !debouncedSearchQuery || 
      request.title.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      request.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
      request.company_name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    
    const matchesStatus = !filterStatus || request.status === filterStatus
    const matchesPriority = !filterPriority || request.priority === filterPriority
    
    return matchesSearch && matchesStatus && matchesPriority
  })

  // Stats calculations
  const stats = {
    total: filteredRequests.length,
    pending: filteredRequests.filter(r => r.status === 'pending').length,
    approved: filteredRequests.filter(r => r.status === 'approved').length,
    rejected: filteredRequests.filter(r => r.status === 'rejected').length,
    completed: filteredRequests.filter(r => r.status === 'completed').length,
    urgent: filteredRequests.filter(r => r.priority === 'urgent').length
  }

  if (isLoading) {
    return (
      <div className="pt-4">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading requests..." />
        </div>
      </div>
    )
  }

  return (
    <div className="pt-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
            Edit Requests
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage client edit requests and review submissions
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-auto pl-10 pr-4 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div className="grid grid-cols-2 sm:flex gap-3">
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-accent-500 focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500 dark:text-neutral-400 pointer-events-none" />
            </div>

            <div className="relative">
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-accent-500 focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-500 dark:text-neutral-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.total}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-md">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Pending</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.pending}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-warning-400 to-warning-500 rounded-xl flex items-center justify-center shadow-md">
                <Clock className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Approved</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.approved}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-success-400 to-success-500 rounded-xl flex items-center justify-center shadow-md">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Rejected</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.rejected}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-error-400 to-error-500 rounded-xl flex items-center justify-center shadow-md">
                <XCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Completed</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.completed}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl flex items-center justify-center shadow-md">
                <Archive className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-neutral-700 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Urgent</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stats.urgent}</p>
              </div>
              <div className="h-10 w-10 bg-gradient-to-br from-error-400 to-error-500 rounded-xl flex items-center justify-center shadow-md">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredRequests.map((request, index) => (
            <motion.div
              key={request.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="p-5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl bg-white dark:bg-[#252232] hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setSelectedRequestId(request.id)}
            >
              <div className="flex flex-col gap-3">
                {/* Top row: priority icon + content + desktop badges */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
                      request.priority === 'urgent' ? 'bg-error-50 dark:bg-error-900/20' :
                      request.priority === 'high' ? 'bg-warning-50 dark:bg-warning-900/20' :
                      request.priority === 'medium' ? 'bg-accent-50 dark:bg-accent-900/20' :
                      'bg-neutral-100 dark:bg-neutral-800'
                    )}>
                      {request.priority === 'urgent' ? <AlertTriangle className="w-4 h-4 text-error-600 dark:text-error-400" /> :
                       request.priority === 'high' ? <TrendingUp className="w-4 h-4 text-warning-600 dark:text-warning-400" /> :
                       request.priority === 'medium' ? <ArrowUpRight className="w-4 h-4 text-accent-600 dark:text-accent-400" /> :
                       <Flag className="w-4 h-4 text-neutral-400" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                          {request.title}
                        </h4>
                        <CategoryBadge category={request.category} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3 flex-shrink-0" />{request.company_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3 flex-shrink-0" />{request.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3 flex-shrink-0" />{request.site_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 flex-shrink-0" />{formatRelativeTime(request.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop: badges + action menu on the right */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                    <PriorityBadge priority={request.priority} />
                    <StatusBadge status={request.status} />
                    <ActionMenu
                      request={request}
                      onApprove={setShowApprovalModal}
                      onReject={setShowRejectionModal}
                      onComplete={(id) => completeMutation.mutate(id)}
                      isCompleting={completeMutation.isLoading}
                    />
                  </div>
                </div>

                {/* Mobile: badges + action menu as a bottom row */}
                <div className="flex sm:hidden items-center justify-between" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <PriorityBadge priority={request.priority} />
                    <StatusBadge status={request.status} />
                  </div>
                  <ActionMenu
                    request={request}
                    onApprove={setShowApprovalModal}
                    onReject={setShowRejectionModal}
                    onComplete={(id) => completeMutation.mutate(id)}
                    isCompleting={completeMutation.isLoading}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="bg-neutral-100 dark:bg-neutral-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-8 w-8 text-neutral-400 dark:text-neutral-500" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            No edit requests found
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
            {searchQuery || filterStatus || filterPriority 
              ? "No requests match your current filters. Try adjusting your search criteria."
              : "Client edit requests will appear here once they are submitted."
            }
          </p>
        </motion.div>
      )}

      {/* Request detail modal */}
      <AnimatePresence>
        {selectedRequestId && (
          <RequestDetailModal
            requestId={selectedRequestId}
            onClose={() => setSelectedRequestId(null)}
            onUpdated={() => queryClient.invalidateQueries('requests')}
          />
        )}
      </AnimatePresence>

      {/* List-level approve / reject modals */}
      <AnimatePresence>
        {showApprovalModal && (
          <ApprovalModal
            request={showApprovalModal}
            onClose={() => setShowApprovalModal(null)}
            onApprove={(data) => approveMutation.mutate({ requestId: showApprovalModal.id, ...data })}
            isLoading={approveMutation.isLoading}
          />
        )}
        {showRejectionModal && (
          <RejectionModal
            request={showRejectionModal}
            onClose={() => setShowRejectionModal(null)}
            onReject={(reason) => rejectMutation.mutate({ requestId: showRejectionModal.id, reason })}
            isLoading={rejectMutation.isLoading}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Request Detail Modal
const RequestDetailModal = ({ requestId, onClose, onUpdated }) => {
  const queryClient = useQueryClient()
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: request, isLoading } = useQuery(
    ['request-detail', requestId],
    () => apiService.get(`/requests/${requestId}`).then(res => res.data),
    { enabled: !!requestId }
  )

  const approveMutation = useMutation(
    ({ notes, estimatedHours, assignedTo, deadline }) =>
      apiService.post(`/requests/${requestId}/approve`, {
        approval_notes: notes,
        estimated_hours: estimatedHours,
        assigned_to: assignedTo,
        deadline
      }),
    {
      onSuccess: () => {
        toast.success('Request approved!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
        setShowApprovalModal(false)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to approve')
    }
  )

  const rejectMutation = useMutation(
    (reason) => apiService.post(`/requests/${requestId}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        toast.success('Request rejected!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
        setShowRejectionModal(false)
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to reject')
    }
  )

  const completeMutation = useMutation(
    () => apiService.post(`/requests/${requestId}/complete`),
    {
      onSuccess: () => {
        toast.success('Request marked as completed!')
        queryClient.invalidateQueries(['request-detail', requestId])
        onUpdated()
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to complete request')
    }
  )

  const deleteMutation = useMutation(
    () => apiService.delete(`/requests/${requestId}`),
    {
      onSuccess: () => {
        toast.success('Request deleted')
        queryClient.invalidateQueries('requests')
        onUpdated()
        onClose()
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete request')
    }
  )

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />

        <motion.div
          className="relative w-full max-w-3xl bg-white dark:bg-[#1a1625] rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" text="Loading request..." />
            </div>
          ) : request ? (
            <>
              {/* Header */}
              <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-[#2f2b41]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">
                      {request.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                      <span className="flex items-center gap-1"><Building className="w-3.5 h-3.5" />{request.company_name}</span>
                      <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{request.site_name}</span>
                      <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{request.created_by_name}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(request.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="hidden sm:block"><StatusBadge status={request.status} /></span>
                    <span className="hidden sm:block"><PriorityBadge priority={request.priority} /></span>
                    <Button onClick={onClose} variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col lg:flex-row gap-6 p-6">

                  {/* Left: content */}
                  <div className="flex-1 min-w-0 space-y-5">
                    {/* Description */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5" /> Description
                      </p>
                      <div className="bg-neutral-50 dark:bg-[#1f1c2c] rounded-xl p-4 border border-neutral-200 dark:border-[#2f2b41]">
                        <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                          {request.description}
                        </p>
                      </div>
                    </div>

                    {/* Screenshot */}
                    {request.screenshot_path && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Attachment</p>
                        <a href={request.screenshot_path} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg text-sm text-accent-600 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/30 transition-colors">
                          <ImageIcon className="w-4 h-4" />
                          <span className="font-medium">View screenshot</span>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {/* Approval notes */}
                    {request.approval_notes && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-success-600 dark:text-success-400 mb-2">Approval Notes</p>
                        <div className="bg-success-50 dark:bg-success-900/20 rounded-xl p-4 border border-success-200 dark:border-success-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.approval_notes}</p>
                        </div>
                      </div>
                    )}

                    {/* Rejection reason */}
                    {request.rejection_reason && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-error-600 dark:text-error-400 mb-2">Rejection Reason</p>
                        <div className="bg-error-50 dark:bg-error-900/20 rounded-xl p-4 border border-error-200 dark:border-error-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.rejection_reason}</p>
                        </div>
                      </div>
                    )}

                    {/* Completion notes */}
                    {request.completion_notes && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-accent-600 dark:text-accent-400 mb-2">Completion Notes</p>
                        <div className="bg-accent-50 dark:bg-accent-900/20 rounded-xl p-4 border border-accent-200 dark:border-accent-800">
                          <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap">{request.completion_notes}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: metadata + actions */}
                  <div className="w-full lg:w-48 flex-shrink-0">
                    <div className="lg:sticky lg:top-0 space-y-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Details</p>
                        <div className="space-y-2 text-xs">
                          {[
                            ['Category', request.category ? <span className="capitalize">{request.category}</span> : null],
                            ['Est. hours', request.estimated_hours ? `${request.estimated_hours}h` : null],
                            ['Deadline', request.deadline ? new Date(request.deadline).toLocaleDateString() : null],
                            ['Submitted', new Date(request.created_at).toLocaleDateString()],
                            ...(request.approved_at ? [['Approved', new Date(request.approved_at).toLocaleDateString()]] : []),
                            ...(request.completed_at ? [['Completed', new Date(request.completed_at).toLocaleDateString()]] : []),
                          ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label} className="flex justify-between gap-2">
                              <span className="text-neutral-500 dark:text-neutral-400 shrink-0">{label}</span>
                              <span className="text-neutral-900 dark:text-neutral-100 text-right">{value}</span>
                            </div>
                          ))}
                          {request.page_url && (
                            <div className="flex justify-between gap-2">
                              <span className="text-neutral-500 dark:text-neutral-400">Page</span>
                              <a href={request.page_url} target="_blank" rel="noopener noreferrer"
                                className="text-accent-600 dark:text-accent-400 hover:underline flex items-center gap-0.5">
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {request.status === 'pending' && (
                        <div className="space-y-2 pt-3 border-t border-neutral-100 dark:border-[#2f2b41]">
                          <Button size="sm" className="w-full bg-success-600 hover:bg-success-700 text-white flex items-center gap-1.5"
                            onClick={() => setShowApprovalModal(true)}>
                            <ThumbsUp className="w-3.5 h-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" className="w-full flex items-center gap-1.5"
                            onClick={() => setShowRejectionModal(true)}>
                            <ThumbsDown className="w-3.5 h-3.5" /> Reject
                          </Button>
                        </div>
                      )}

                      {request.status === 'approved' && (
                        <div className="pt-3 border-t border-neutral-100 dark:border-[#2f2b41] space-y-2">
                          <Button size="sm" asChild className="w-full">
                            <Link to={`/admin/editor?request=${request.id}`}>
                              <Edit className="w-3.5 h-3.5 mr-1.5" /> Open in Editor
                            </Link>
                          </Button>
                          <Button size="sm" className="w-full bg-accent-600 hover:bg-accent-700 text-white flex items-center gap-1.5"
                            onClick={() => completeMutation.mutate()}
                            disabled={completeMutation.isLoading}>
                            <CheckCircle className="w-3.5 h-3.5" /> Mark Complete
                          </Button>
                        </div>
                      )}

                      {/* Delete */}
                      <div className="pt-3 border-t border-neutral-100 dark:border-[#2f2b41]">
                        {showDeleteConfirm ? (
                          <div className="space-y-2">
                            <p className="text-xs text-error-600 dark:text-error-400 font-medium">Delete this request?</p>
                            <div className="flex gap-2">
                              <Button size="sm" variant="destructive" className="flex-1"
                                onClick={() => deleteMutation.mutate()}
                                disabled={deleteMutation.isLoading}>
                                {deleteMutation.isLoading ? 'Deleting...' : 'Confirm'}
                              </Button>
                              <Button size="sm" variant="outline" className="flex-1"
                                onClick={() => setShowDeleteConfirm(false)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="w-full text-error-600 border-error-200 hover:bg-error-50 dark:text-error-400 dark:border-error-800 dark:hover:bg-error-900/20 flex items-center gap-1.5"
                            onClick={() => setShowDeleteConfirm(true)}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete Request
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </>
          ) : null}
        </motion.div>

      <AnimatePresence>
        {showApprovalModal && request && (
          <ApprovalModal
            request={request}
            onClose={() => setShowApprovalModal(false)}
            onApprove={(data) => approveMutation.mutate(data)}
            isLoading={approveMutation.isLoading}
          />
        )}
        {showRejectionModal && request && (
          <RejectionModal
            request={request}
            onClose={() => setShowRejectionModal(false)}
            onReject={(reason) => rejectMutation.mutate(reason)}
            isLoading={rejectMutation.isLoading}
          />
        )}
      </AnimatePresence>
    </div>,
    document.body
  )
}

// Approval Modal Component
const ApprovalModal = ({ request, onClose, onApprove, isLoading }) => {
  const [formData, setFormData] = useState({
    notes: '',
    estimatedHours: '',
    deadline: '',
    assignedTo: ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onApprove({
      notes: formData.notes,
      estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
      deadline: formData.deadline || undefined,
      assignedTo: formData.assignedTo || undefined
    })
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all"
          onClick={onClose}
        />
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 inline-block align-bottom text-left shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625] rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-[#1a1625] px-6 pt-6 pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-success-100 dark:bg-success-900 sm:mx-0 sm:h-10 sm:w-10">
                  <CheckCircle className="h-6 w-6 text-success-600 dark:text-success-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-semibold text-neutral-900 dark:text-neutral-100">
                    Approve Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      You are about to approve "{request.title}". Set the estimated hours and deadline for this request.
                    </p>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Approval Notes (Optional)
                        </label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                          placeholder="Add any notes for the client..."
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            Estimated Hours
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            min="0"
                            value={formData.estimatedHours}
                            onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: e.target.value }))}
                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                            placeholder="2.5"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2 flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            Deadline
                          </label>
                          <ModernDateTimeInput
                            value={formData.deadline}
                            onChange={(value) => setFormData(prev => ({ ...prev, deadline: value }))}
                            placeholder="Select deadline"
                            includeTime={true}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-success-600 hover:bg-success-700 text-white"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Approving...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="h-4 w-4" />
                    <span>Approve Request</span>
                  </div>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

// Rejection Modal Component
const RejectionModal = ({ request, onClose, onReject, isLoading }) => {
  const [reason, setReason] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!reason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }
    onReject(reason)
  }

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-sm transition-all"
          onClick={onClose}
        />
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 inline-block align-bottom text-left shadow-2xl border-0 transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full bg-white dark:bg-[#1a1625] rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-[#1a1625] px-6 pt-6 pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-error-100 dark:bg-error-900 sm:mx-0 sm:h-10 sm:w-10">
                  <XCircle className="h-6 w-6 text-error-600 dark:text-error-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-semibold text-neutral-900 dark:text-neutral-100">
                    Reject Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      You are about to reject "{request.title}". Please provide a reason for the client.
                    </p>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Rejection Reason *
                      </label>
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                        placeholder="Explain why this request is being rejected..."
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-neutral-50/50 dark:bg-[#1f1c2c] border-t border-neutral-200 dark:border-[#2f2b41] px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
              <Button
                type="submit"
                variant="destructive"
                disabled={isLoading || !reason.trim()}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Rejecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ThumbsDown className="h-4 w-4" />
                    <span>Reject Request</span>
                  </div>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}

export default RequestManager